// Bem-Estar > Atividade: passos, distância, calorias, exercício, batimentos e sono vindos do relógio
// (Health Connect no Android, app Saúde no iPhone), com meta de passos, os últimos 7 dias e digitação à mão.
import { useCallback, useEffect, useState } from 'react';
import { Alert, Linking, Platform, Pressable, ScrollView, Text, View } from 'react-native';
import { useDoc } from '../store/store';
import { metaPassos, registrarAMao } from '../store/bemestar';
import type { DiaRelogio } from '../store/types';
import {
  LINK_HC, abrirAjustesSaude, conectarRelogio, desconectarRelogio, fonteDoAparelho, situacaoRelogio, sincronizarRelogio, type SituacaoRelogio,
} from '../lib/relogio';
import { semBloqueio } from '../auth/AuthContext';
import { Btn, Chip, Field, Sheet } from './ui';
import { C, alpha } from './theme';
import { addDays, brDateTime, parseYmd, today } from '../lib/date';
import { t } from '../lib/i18n';
import { ROLAGEM_TECLADO } from './teclado';

const LETRAS = ['@letra-dom', '@letra-seg', '@letra-ter', '@letra-qua', '@letra-qui', '@letra-sex', '@letra-sab'];
const num = (n: number) => n.toLocaleString();
const hm = (min: number) => `${Math.floor(min / 60)}h${String(min % 60).padStart(2, '0')}`;
const AZUL = '#22C55E';

function Bloco({ icon, valor, rotulo }: { icon: string; valor: string; rotulo: string }) {
  return (
    <View style={{ width: '31.5%', backgroundColor: C.card, borderRadius: 12, padding: 10, gap: 2 }}>
      <Text style={{ fontSize: 16 }}>{icon}</Text>
      <Text style={{ color: C.text, fontWeight: '800', fontSize: 15 }} numberOfLines={1}>{valor}</Text>
      <Text style={{ color: C.sub, fontSize: 10 }} numberOfLines={2}>{rotulo}</Text>
    </View>
  );
}

export function Atividade({ cor }: { cor: string }) {
  const d = useDoc('bemestar');
  const hoje = today();
  const dia: DiaRelogio | undefined = d.relogio?.[hoje];
  const meta = d.metaPassos ?? 8000;
  const passos = dia?.passos ?? 0;
  const [sit, setSit] = useState<SituacaoRelogio | null>(null);
  const [lendo, setLendo] = useState(false);
  const [ajuda, setAjuda] = useState(false);
  const [mao, setMao] = useState(false);

  const atualizar = useCallback(async (forcar = false) => {
    const s = await situacaoRelogio(); setSit(s);
    if (s !== 'pronto') return;
    setLendo(true);
    try { await sincronizarRelogio(forcar); } finally { setLendo(false); }
  }, []);
  useEffect(() => { atualizar(); }, [atualizar]);

  const conectar = async () => {
    try {
      const ok = await semBloqueio(conectarRelogio);
      setAjuda(false);
      if (!ok) { Alert.alert(t('Sem permissão'), t('Para ler o relógio, libere os dados de atividade para o My Planner.')); return; }
      await atualizar(true);
    } catch (e: any) { Alert.alert(t('Não foi possível'), String(e?.message ?? e)); }
  };

  const semana = Array.from({ length: 7 }, (_, i) => addDays(hoje, i - 6));
  const maxP = Math.max(meta, ...semana.map(x => d.relogio?.[x]?.passos ?? 0));
  const pct = Math.min(1, passos / meta);
  const temAlgo = !!dia && Object.keys(dia).some(k => !['fonte', 'apps', 'em'].includes(k));
  const [aberto, setAberto] = useState(false);

  // fechado: só uma linha com os passos (ocupa pouco espaço); tocar abre tudo
  if (!aberto) return (
    <Pressable onPress={() => setAberto(true)} style={{ backgroundColor: alpha(AZUL, 0.1), borderRadius: 14, paddingHorizontal: 12, paddingVertical: 10, gap: 6, borderWidth: 1, borderColor: alpha(AZUL, 0.35) }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Text style={{ fontSize: 18 }}>⌚</Text>
        <Text style={{ color: C.text, fontWeight: '800' }}>{num(passos)}</Text>
        <Text style={{ color: C.sub, fontSize: 12, flex: 1 }} numberOfLines={1}>
          {t('de {meta} passos', { meta: num(meta) })}{dia?.exMin ? ` · 🏃 ${dia.exMin} min` : ''}{dia?.kcal ? ` · 🔥 ${num(dia.kcal)}` : ''}{dia?.sonoMin ? ` · 😴 ${hm(dia.sonoMin)}` : ''}</Text>
        <Text style={{ color: AZUL, fontSize: 12, fontWeight: '700' }}>{t('Ver mais')} ▾</Text>
      </View>
      <View style={{ height: 6, borderRadius: 3, backgroundColor: C.card2, overflow: 'hidden' }}>
        <View style={{ width: `${pct * 100}%`, height: '100%', backgroundColor: AZUL }} />
      </View>
    </Pressable>
  );

  return (
    <View style={{ backgroundColor: alpha(AZUL, 0.1), borderRadius: 18, padding: 14, gap: 10, borderWidth: 1, borderColor: alpha(AZUL, 0.35) }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <Text style={{ fontSize: 28 }}>⌚</Text>
        <View style={{ flex: 1 }}>
          <Text style={{ color: C.text, fontWeight: '800', fontSize: 16 }}>{t('Atividade hoje')}</Text>
          <Text style={{ color: C.sub, fontSize: 11 }} numberOfLines={2}>
            {lendo ? t('Lendo o relógio...') : dia?.em ? t('{fonte} · {data}', { fonte: dia.fonte === 'manual' ? t('Digitado') : t(dia.fonte), data: brDateTime(dia.em) })
              : t('Passos, exercício, calorias, batimentos e sono')}
          </Text>
        </View>
        {sit === 'pronto' && <Pressable hitSlop={8} onPress={() => atualizar(true)}><Text style={{ color: AZUL, fontWeight: '700' }}>↻ {t('Atualizar')}</Text></Pressable>}
        <Pressable hitSlop={8} onPress={() => setAberto(false)}><Text style={{ color: C.sub, fontSize: 12, marginLeft: 8 }}>{t('Fechar')} ▴</Text></Pressable>
      </View>

      <View style={{ gap: 6 }}>
        <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 6 }}>
          <Text style={{ color: C.text, fontSize: 28, fontWeight: '900' }}>{num(passos)}</Text>
          <Text style={{ color: C.sub }}>{t('de {meta} passos', { meta: num(meta) })}{passos >= meta ? ' · ' + t('meta batida!') + ' 🎉' : ''}</Text>
        </View>
        <View style={{ height: 10, borderRadius: 5, backgroundColor: C.card2, overflow: 'hidden' }}>
          <View style={{ width: `${pct * 100}%`, height: '100%', backgroundColor: AZUL, borderRadius: 5 }} />
        </View>
      </View>

      {temAlgo && (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
          <Bloco icon="📏" valor={dia?.distM ? `${(dia.distM / 1000).toFixed(1)} km` : '-'} rotulo={t('Distância')} />
          <Bloco icon="🔥" valor={dia?.kcal ? `${num(dia.kcal)} kcal` : '-'} rotulo={t('Calorias em atividade')} />
          <Bloco icon="🏃" valor={dia?.exMin ? `${dia.exMin} min` : '-'} rotulo={t('Exercício')} />
          <Bloco icon="❤️" valor={dia?.fc ? `${dia.fc} bpm` : '-'} rotulo={dia?.fcRep ? t('Média · repouso {n}', { n: dia.fcRep }) : t('Batimentos (média)')} />
          <Bloco icon="😴" valor={dia?.sonoMin ? hm(dia.sonoMin) : '-'} rotulo={t('Sono da noite')} />
          <Bloco icon="🎯" valor={`${Math.round(pct * 100)}%`} rotulo={t('Da meta de passos')} />
        </View>
      )}

      {/* últimos 7 dias */}
      <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 6, height: 78 }}>
        {semana.map(x => {
          const p = d.relogio?.[x]?.passos ?? 0;
          const bateu = p >= meta;
          return (
            <View key={x} style={{ flex: 1, alignItems: 'center', gap: 3 }}>
              <View style={{ width: '70%', height: Math.max(3, (p / maxP) * 52), borderRadius: 4, backgroundColor: bateu ? AZUL : alpha(AZUL, x === hoje ? 0.7 : 0.4) }} />
              <Text style={{ color: x === hoje ? C.text : C.sub, fontSize: 10, fontWeight: x === hoje ? '800' : '400' }}>{t(LETRAS[parseYmd(x).getDay()])}</Text>
            </View>
          );
        })}
      </View>

      {sit === 'sem-suporte' && <Text style={{ color: C.sub, fontSize: 12 }}>{Platform.OS === 'web'
        ? t('Para ler o relógio, conecte pelo app no celular. Os números aparecem aqui também.') : t('Este dispositivo não tem dados de saúde. Dá para digitar à mão.')}</Text>}
      {sit === 'sem-app-novo' && <Text style={{ color: C.sub, fontSize: 12 }}>{t('Para ler o relógio automaticamente, instale a versão nova do app (0.33). Enquanto isso, dá para digitar à mão.')}</Text>}
      {sit === 'instalar-hc' && (<>
        <Text style={{ color: C.sub, fontSize: 12 }}>{t('Falta o Health Connect, o app do Google que junta os dados dos relógios e apps de saúde.')}</Text>
        <Btn cor={AZUL} label={t('Instalar ou atualizar o Health Connect')} onPress={() => semBloqueio(() => Linking.openURL(LINK_HC)).catch(() => {})} />
      </>)}
      {sit === 'desconectado' && <Btn cor={AZUL} label={'⌚ ' + t('Conectar relógio ou app de saúde')} onPress={() => setAjuda(true)} />}

      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Chip small cor={AZUL} on={false} label={'✏️ ' + t('Digitar à mão')} onPress={() => setMao(true)} />
        {sit === 'pronto' && <Chip small cor={AZUL} on={false} label={t('Permissões')} onPress={() => semBloqueio(async () => abrirAjustesSaude()).catch(() => {})} />}
        {sit === 'pronto' && <Chip small cor={AZUL} on={false} label={t('Como funciona')} onPress={() => setAjuda(true)} />}
        <View style={{ flex: 1 }} />
        <Text style={{ color: C.sub, fontSize: 11 }}>{t('Meta')}</Text>
        <Chip small cor={AZUL} on={false} label="−" onPress={() => metaPassos(meta - 1000)} />
        <Text style={{ color: C.text, fontWeight: '700', fontSize: 12 }}>{num(meta)}</Text>
        <Chip small cor={AZUL} on={false} label="+" onPress={() => metaPassos(meta + 1000)} />
      </View>

      <ComoConectar visible={ajuda} conectado={sit === 'pronto'} onClose={() => setAjuda(false)} onConectar={conectar}
        onDesconectar={async () => { await desconectarRelogio(); setAjuda(false); setSit('desconectado'); }} />
      {mao && <DigitarAMao dia={dia} cor={cor} onClose={() => setMao(false)} />}
    </View>
  );
}

function ComoConectar({ visible, conectado, onClose, onConectar, onDesconectar }:
  { visible: boolean; conectado: boolean; onClose: () => void; onConectar: () => void; onDesconectar: () => void }) {
  const ios = Platform.OS === 'ios';
  const passos = ios ? [
    t('Apple Watch: já grava tudo no app Saúde do celular. Não precisa fazer nada.'),
    t('Outros relógios (Garmin, Samsung, Xiaomi, Amazfit...): no app do relógio, ligue a opção de enviar os dados para o app Saúde (Apple Health).'),
    t('Toque em Conectar e, na tela do app Saúde, ligue os itens que o My Planner pode ler.'),
  ] : [
    t('No app do seu relógio (Samsung Health, Garmin Connect, Mi Fitness, Zepp, Fitbit, Amazfit, Google Fit...), ligue a opção "Health Connect" ou "Sincronizar com o Health Connect".'),
    t('Toque em Conectar e, na tela do Health Connect, permita que o My Planner leia os dados.'),
    t('Alguns relógios (como os da Huawei) podem não enviar dados para o Health Connect. Nesse caso, digite à mão.'),
  ];
  return (
    <Sheet visible={visible} onClose={onClose} title={t('Relógio e apps de saúde')}>
      <ScrollView contentContainerStyle={{ padding: 16, gap: 12, paddingBottom: 60 }}>
        <Text style={{ color: C.text }}>{t('O My Planner lê os dados do relógio pelo {fonte}, onde o relógio e os apps de saúde e de exercício guardam tudo.', { fonte: fonteDoAparelho() })}</Text>
        {passos.map((p, i) => (
          <View key={i} style={{ flexDirection: 'row', gap: 10 }}>
            <Text style={{ color: AZUL, fontWeight: '900', width: 18 }}>{i + 1}</Text>
            <Text style={{ color: C.text, flex: 1 }}>{p}</Text>
          </View>
        ))}
        <Text style={{ color: C.sub, fontSize: 12 }}>{t('O app só lê (passos, distância, calorias, exercício, batimentos e sono) e nunca altera os seus dados de saúde. Os totais de cada dia ficam na sua conta para aparecer também no site e no outro celular.')}</Text>
        {conectado ? (<>
          <Btn cor={AZUL} label={t('Ler de novo agora')} onPress={onConectar} />
          <Btn outline cor={C.danger} label={t('Desconectar')} onPress={onDesconectar} />
        </>) : <Btn cor={AZUL} label={t('Conectar')} onPress={onConectar} />}
      </ScrollView>
    </Sheet>
  );
}

function DigitarAMao({ dia, cor, onClose }: { dia?: DiaRelogio; cor: string; onClose: () => void }) {
  const [data, setData] = useState(today());
  const [v, setV] = useState({ passos: String(dia?.passos ?? ''), exMin: String(dia?.exMin ?? ''), kcal: String(dia?.kcal ?? ''), sono: dia?.sonoMin ? String(Math.round(dia.sonoMin / 6) / 10) : '' });
  const n = (s: string) => { const x = Number(s.replace(',', '.')); return isFinite(x) && x > 0 ? x : undefined; };
  const salvar = () => {
    registrarAMao(data, { passos: n(v.passos) && Math.round(n(v.passos)!), exMin: n(v.exMin) && Math.round(n(v.exMin)!), kcal: n(v.kcal) && Math.round(n(v.kcal)!),
      sonoMin: n(v.sono) && Math.round(n(v.sono)! * 60) });
    onClose();
  };
  return (
    <Sheet visible onClose={onClose} title={t('Digitar à mão')} right={<Pressable onPress={salvar}><Text style={{ color: cor, fontWeight: '800' }}>{t('Salvar')}</Text></Pressable>}>
      <ScrollView {...ROLAGEM_TECLADO} keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 10, paddingBottom: 60 }}>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Chip cor={cor} on={data === today()} label={t('Hoje')} onPress={() => setData(today())} />
          <Chip cor={cor} on={data === addDays(today(), -1)} label={t('Ontem')} onPress={() => setData(addDays(today(), -1))} />
        </View>
        <Text style={{ color: C.sub, fontSize: 12 }}>{t('Passos')}</Text>
        <Field value={v.passos} onChangeText={x => setV({ ...v, passos: x })} keyboardType="number-pad" placeholder="8000" />
        <Text style={{ color: C.sub, fontSize: 12 }}>{t('Minutos de exercício')}</Text>
        <Field value={v.exMin} onChangeText={x => setV({ ...v, exMin: x })} keyboardType="number-pad" placeholder="30" />
        <Text style={{ color: C.sub, fontSize: 12 }}>{t('Calorias gastas em atividade (kcal)')}</Text>
        <Field value={v.kcal} onChangeText={x => setV({ ...v, kcal: x })} keyboardType="number-pad" placeholder="250" />
        <Text style={{ color: C.sub, fontSize: 12 }}>{t('Horas de sono')}</Text>
        <Field value={v.sono} onChangeText={x => setV({ ...v, sono: x })} keyboardType="decimal-pad" placeholder="7,5" />
        <Btn cor={cor} label={t('Salvar')} onPress={salvar} />
        <Text style={{ color: C.mute, fontSize: 11 }}>{t('Se o relógio estiver conectado, os números dele entram por cima dos digitados quando ele tiver o dado.')}</Text>
      </ScrollView>
    </Sheet>
  );
}
